import { useState } from 'react';
import type { ExcelValueConflict } from '../../utils/excel-import-conflicts';
import BulkTableModal from './bulk-table/BulkTableModal';
import ExcelConflictFields from './bulk-table/ExcelConflictFields';
import ExcelToolbar, { ExcelIcon, InlineExcelActionButton } from './bulk-table/ExcelToolbar';

export type ExcelImportReadResult<T> =
  | {
      status: 'ready';
      values: T;
      conflicts?: ExcelValueConflict[];
      resolveConflicts?: (selected: Record<string, unknown>) => T;
    }
  | { status: 'invalid-template' }
  | {
      status: 'rejected';
      message: string;
      downloadRejectedFile: () => void | Promise<void>;
    };

type Props<T> = {
  title: string;
  disabled?: boolean;
  confirmImport?: boolean;
  showImportErrorsInModal?: boolean;
  onDownload: () => void | Promise<void>;
  readFiles: (files: File[]) => Promise<ExcelImportReadResult<T>>;
  onImport: (values: T) => void | Promise<void>;
};

export default function ExcelImportToolbar<T>({
  title,
  disabled = false,
  confirmImport: shouldConfirmImport = true,
  showImportErrorsInModal = false,
  onDownload,
  readFiles,
  onImport,
}: Props<T>) {
  const [pendingImport, setPendingImport] = useState<T>();
  const [conflictImport, setConflictImport] =
    useState<Extract<ExcelImportReadResult<T>, { status: 'ready' }>>();
  const [selections, setSelections] = useState<Record<string, string>>({});
  const [error, setError] = useState('');
  const [downloadRejectedFile, setDownloadRejectedFile] = useState<
    (() => void | Promise<void>) | undefined
  >();
  const [isTemplateError, setIsTemplateError] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [isErrorModalOpen, setIsErrorModalOpen] = useState(false);

  const setImportError = (message: string, templateError = false, openModal = false) => {
    setError(message);
    setIsTemplateError(templateError);
    setPendingImport(undefined);
    setIsErrorModalOpen(openModal);
  };

  const clearImportError = () => {
    setError('');
    setIsTemplateError(false);
    setDownloadRejectedFile(undefined);
    setIsErrorModalOpen(false);
  };

  const handleDownload = async () => {
    clearImportError();
    try {
      await onDownload();
    } catch (downloadError) {
      setImportError(
        downloadError instanceof Error
          ? downloadError.message
          : 'The XLSX file could not be created.',
      );
    }
  };

  const importValues = async (values: T) => {
    setPendingImport(undefined);
    setError('');
    setIsImporting(true);
    try {
      await onImport(values);
    } catch (importError) {
      setImportError(
        importError instanceof Error ? importError.message : 'The XLSX file could not be saved.',
        false,
        showImportErrorsInModal,
      );
    } finally {
      setIsImporting(false);
    }
  };

  const handleFiles = async (files: File[]) => {
    clearImportError();
    setIsImporting(true);
    try {
      const result = await readFiles(files);
      if (result.status === 'invalid-template') {
        setImportError(
          'The template you uploaded is out of date or has been modified.',
          true,
          showImportErrorsInModal,
        );
        return;
      }
      if (result.status === 'rejected') {
        setImportError(result.message, false, showImportErrorsInModal);
        setDownloadRejectedFile(() => result.downloadRejectedFile);
        return;
      }
      if (result.conflicts?.length) {
        setSelections({});
        setConflictImport(result);
        return;
      }
      if (shouldConfirmImport) {
        setPendingImport(result.values);
        return;
      }
      await importValues(result.values);
    } catch (importError) {
      setImportError(
        importError instanceof Error ? importError.message : 'The XLSX file could not be imported.',
        false,
        showImportErrorsInModal,
      );
    } finally {
      setIsImporting(false);
    }
  };

  const confirmImport = async () => {
    if (pendingImport === undefined) return;
    await importValues(pendingImport);
  };

  const errorDetails = error && (
    <div className="govuk-!-margin-bottom-4">
      <p className="govuk-error-message govuk-!-margin-bottom-2" role="alert">
        <span className="govuk-visually-hidden">Error:</span>{' '}
        {isTemplateError ? (
          <>
            The template you uploaded is out of date or has been modified. Please download the
            latest template version using the{' '}
            <InlineExcelActionButton
              type="button"
              aria-label={`Download latest ${title} Excel template`}
              title={`Download latest ${title} Excel template`}
              onClick={() => void handleDownload()}
            >
              <ExcelIcon />
            </InlineExcelActionButton>{' '}
            and try again.
          </>
        ) : (
          error
        )}
      </p>
      {downloadRejectedFile && (
        <button
          className="govuk-button govuk-button--secondary govuk-!-margin-bottom-0"
          type="button"
          onClick={() => void downloadRejectedFile()}
        >
          Download file with errors
        </button>
      )}
    </div>
  );

  return (
    <>
      <ExcelToolbar
        title={title}
        disabled={disabled || isImporting}
        onDownload={handleDownload}
        onFiles={handleFiles}
      />
      {error && !isErrorModalOpen && errorDetails}
      {conflictImport && (
        <BulkTableModal
          title="Resolve Excel conflicts"
          onClose={() => setConflictImport(undefined)}
        >
          <ExcelConflictFields
            conflicts={conflictImport.conflicts || []}
            selections={selections}
            onChange={setSelections}
          />
          <div className="govuk-button-group">
            <button
              type="button"
              className="govuk-button"
              disabled={conflictImport.conflicts?.some((conflict) => !selections[conflict.key])}
              onClick={() => {
                const selected = Object.fromEntries(
                  (conflictImport.conflicts || []).map((conflict) => [
                    conflict.key,
                    conflict.options[Number(selections[conflict.key])].value,
                  ]),
                );
                const values = conflictImport.resolveConflicts!(selected);
                setConflictImport(undefined);
                if (shouldConfirmImport) setPendingImport(values);
                else void importValues(values);
              }}
            >
              Continue
            </button>
            <button
              type="button"
              className="govuk-button govuk-button--secondary"
              onClick={() => setConflictImport(undefined)}
            >
              Cancel
            </button>
          </div>
        </BulkTableModal>
      )}
      {error && isErrorModalOpen && (
        <BulkTableModal title="Upload errors" onClose={clearImportError}>
          {errorDetails}
          <button className="govuk-button" type="button" onClick={clearImportError}>
            Close
          </button>
        </BulkTableModal>
      )}
      {pendingImport !== undefined && (
        <BulkTableModal
          title="Would you like to import this dataset?"
          onClose={() => setPendingImport(undefined)}
        >
          <p className="govuk-hint">This will overwrite any existing values.</p>
          <div className="govuk-button-group">
            <button className="govuk-button" type="button" onClick={() => void confirmImport()}>
              Yes
            </button>
            <button
              className="govuk-button govuk-button--secondary"
              type="button"
              onClick={() => setPendingImport(undefined)}
            >
              No
            </button>
          </div>
        </BulkTableModal>
      )}
    </>
  );
}
